import React, { useState, useEffect } from 'react';
import { Card, Spinner, Alert, Collapse } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faMoneyBillWave, faFileInvoiceDollar, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import api from '../../api';

const STATI_RIEPILOGO = [
    { key: 'Da Evadere', label: 'Ordini da evadere', color: '#dc3545', defaultOpen: true },
    { key: 'Parz.Concluso', label: 'Ordini parz. conclusi', color: '#ffc107', defaultOpen: false },
    { key: 'Concluso', label: 'Ordini conclusi', color: '#28a745', defaultOpen: false },
];

const fmtEuro = (v) => `€ ${(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Badge({ icon, label, value, color, onClick }) {
    return (
        <div
            onClick={onClick}
            className="mdh-badge d-flex flex-column align-items-center justify-content-center bg-white shadow-sm rounded-3 p-2"
            style={{ flex: 1, borderTop: `3px solid ${color}`, cursor: 'pointer', minHeight: 78 }}
        >
            <FontAwesomeIcon icon={icon} style={{ color }} className="mb-1" />
            <div className="fw-bold" style={{ fontSize: '1rem', color }}>{value}</div>
            <div className="text-muted text-center" style={{ fontSize: '0.7rem', lineHeight: 1.1 }}>{label}</div>
        </div>
    );
}

function RiepilogoOrigini({ label, color, origini, defaultOpen }) {
    const [open, setOpen] = useState(defaultOpen);
    const totale = origini.reduce((s, o) => s + o.totale_fatturato, 0);
    const massimo = origini.reduce((m, o) => Math.max(m, o.totale_fatturato), 0) || 1;

    return (
        <Card className="mb-3 shadow-sm border-0">
            <Card.Header
                onClick={() => setOpen(o => !o)}
                className="bg-white d-flex justify-content-between align-items-center"
                style={{ borderLeft: `4px solid ${color}`, cursor: 'pointer' }}
            >
                <strong style={{ fontSize: '0.9rem' }}>{label}</strong>
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: '0.8rem' }}>{fmtEuro(totale)}</span>
                    <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className="text-muted" size="sm" />
                </div>
            </Card.Header>
            <Collapse in={open}>
                <div>
                    <Card.Body className="p-2">
                        {origini.length === 0 ? (
                            <div className="text-muted text-center py-2" style={{ fontSize: '0.85rem' }}>Nessun ordine</div>
                        ) : (
                            origini.map((o) => (
                                <div key={o.origine} className="position-relative py-1 border-bottom overflow-hidden">
                                    <div
                                        className="position-absolute top-0 start-0 h-100 mdh-bar"
                                        style={{
                                            width: `${(o.totale_fatturato / massimo) * 100}%`,
                                            backgroundColor: color,
                                            opacity: 0.12,
                                        }}
                                    />
                                    <div className="position-relative d-flex justify-content-between align-items-center">
                                        <span style={{ fontSize: '0.85rem' }}>{o.origine} <span className="text-muted">({o.count})</span></span>
                                        <strong style={{ fontSize: '0.85rem' }}>{fmtEuro(o.totale_fatturato)}</strong>
                                    </div>
                                </div>
                            ))
                        )}
                    </Card.Body>
                </div>
            </Collapse>
        </Card>
    );
}

function MobileDashboardHome() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get('/statistics/mobile-dashboard')
            .then(res => setData(res.data))
            .catch(() => setError('Errore nel caricamento del riepilogo'));
    }, []);

    if (error) {
        return <Alert variant="danger" className="mb-4">{error}</Alert>;
    }

    if (!data) {
        return (
            <div className="d-flex justify-content-center py-4">
                <Spinner animation="border" size="sm" />
            </div>
        );
    }

    return (
        <div className="mb-4">
            <style>{`
                .mdh-badge { transition: transform 0.12s ease, box-shadow 0.12s ease; }
                .mdh-badge:active { transform: scale(0.95); }
                .mdh-bar { transition: width 0.4s ease; }
            `}</style>
            <div className="d-flex gap-2 mb-3">
                <Badge
                    icon={faShoppingCart}
                    label="Ordini aperti"
                    value={data.ordini_aperti.count}
                    color="#007bff"
                    onClick={() => navigate('/ordini?quick=in_lavorazione')}
                />
                <Badge
                    icon={faMoneyBillWave}
                    label="Da incassare IBS"
                    value={fmtEuro(data.ibs_da_incassare.amount)}
                    color="#ffc107"
                    onClick={() => navigate('/integrazioni/ibs/pagamenti')}
                />
                <Badge
                    icon={faFileInvoiceDollar}
                    label="Nuova Prima Nota"
                    value="+"
                    color="#20c997"
                    onClick={() => navigate('/contabilita/prima-nota?nuovo=1')}
                />
            </div>

            {STATI_RIEPILOGO.map(s => (
                <RiepilogoOrigini
                    key={s.key}
                    label={s.label}
                    color={s.color}
                    origini={data.riepilogo_ordini[s.key] || []}
                    defaultOpen={s.defaultOpen}
                />
            ))}
        </div>
    );
}

export default MobileDashboardHome;
